/**
 * Bandeau admin : demandes portail client (aperçu + traitement Accepter / Refuser / Alternative).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { AlertCircle, ClipboardList, Flame, X } from "lucide-react";
import { api } from "../../lib/axios";
import { getPlansAbonnement, type PlanAbonnement } from "../../lib/api/plansAbonnement";
import { useAuth } from "../../context/AuthContext";

type Summary = {
  enAttente: number;
  parType: { MODIFICATION_PASSAGE: number; INTERVENTION_URGENTE: number };
};

export type DemandeClientRow = {
  id: number;
  typeDemande: string;
  statut: string;
  motif: string;
  niveauUrgence: string;
  createdAt: string;
  passageId: number | null;
  ancienneDate: string | null;
  nouvelleDateDemandee: string | null;
  creneauDemande: string | null;
  adresseDemande: string | null;
  commentaireAdmin?: string | null;
  dateAlternativeProposee?: string | null;
  creneauAlternatif?: string | null;
  treatedAt?: string | null;
  client?: { id: number; nom?: string | null; nomEntreprise?: string | null };
  passage?: { id: number; datePrevue: string; statut: string; contratId: number } | null;
  passageCree?: { id: number; datePrevue: string; statut: string } | null;
  treatedBy?: { id: number; nom?: string | null; prenom?: string | null } | null;
};

type ModalState =
  | { kind: "accepter"; demand: DemandeClientRow }
  | { kind: "refuser"; demand: DemandeClientRow }
  | { kind: "alternative"; demand: DemandeClientRow };

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

type Props = {
  crossLinkTo: string;
  crossLinkLabel: string;
};

export function ClientDemandesAdminPanel({
  crossLinkTo,
  crossLinkLabel,
}: Props): React.JSX.Element {
  const { user } = useAuth();
  const canTreat = useMemo(() => {
    if (!user) return false;
    const r = String(user.role).toLowerCase();
    return r === "admin" || r === "planificateur" || r === "superviseur";
  }, [user]);

  const [summary, setSummary] = useState<Summary | null>(null);
  const [preview, setPreview] = useState<DemandeClientRow[]>([]);
  const [loadErr, setLoadErr] = useState(false);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [drawerDemand, setDrawerDemand] = useState<DemandeClientRow | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [contratIdInput, setContratIdInput] = useState("");
  const [datePrevueInput, setDatePrevueInput] = useState("");
  const [commentAccepter, setCommentAccepter] = useState("");
  const [commentRefuser, setCommentRefuser] = useState("");
  const [commentAlt, setCommentAlt] = useState("");
  const [dateAlt, setDateAlt] = useState("");
  const [creneauAlt, setCreneauAlt] = useState("");

  const [urgPlansCatalog, setUrgPlansCatalog] = useState<PlanAbonnement[]>([]);
  const [urgPlansLoading, setUrgPlansLoading] = useState(false);
  const [urgTarifReferentPlanId, setUrgTarifReferentPlanId] = useState("");
  const [urgTarifMontant, setUrgTarifMontant] = useState("");
  const [urgTarifDevise, setUrgTarifDevise] = useState("");

  const load = useCallback(async () => {
    setLoadErr(false);
    try {
      const [sumRes, listRes] = await Promise.all([
        api.get<{ enAttente?: number; parType?: Summary["parType"] }>("/demandes-client/summary"),
        api.get<{ items?: DemandeClientRow[] }>("/demandes-client", {
          params: { enAttenteSeulement: "true", limit: 20, page: 1 },
        }),
      ]);
      const enAttente = sumRes.data?.enAttente ?? 0;
      const parType = sumRes.data?.parType ?? {
        MODIFICATION_PASSAGE: 0,
        INTERVENTION_URGENTE: 0,
      };
      setSummary({ enAttente, parType });
      setPreview(Array.isArray(listRes.data?.items) ? listRes.data.items : []);
    } catch {
      setLoadErr(true);
      setSummary(null);
      setPreview([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!modal || modal.kind !== "accepter" || modal.demand.typeDemande !== "INTERVENTION_URGENTE") {
      setUrgPlansCatalog([]);
      return;
    }
    let cancelled = false;
    setUrgPlansLoading(true);
    void (async () => {
      try {
        const rows = await getPlansAbonnement();
        if (!cancelled) setUrgPlansCatalog(rows.filter((p) => p.actif !== false));
      } catch {
        if (!cancelled) setUrgPlansCatalog([]);
      } finally {
        if (!cancelled) setUrgPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modal]);

  const openAccepter = (demand: DemandeClientRow) => {
    setContratIdInput(demand.passage?.contratId ? String(demand.passage.contratId) : "");
    setDatePrevueInput(
      demand.nouvelleDateDemandee ? demand.nouvelleDateDemandee.slice(0, 16) : ""
    );
    setCommentAccepter("");
    setUrgTarifReferentPlanId("");
    setUrgTarifMontant("");
    setUrgTarifDevise("");
    setModal({ kind: "accepter", demand });
  };

  const openRefuser = (demand: DemandeClientRow) => {
    setCommentRefuser("");
    setModal({ kind: "refuser", demand });
  };

  const openAlternative = (demand: DemandeClientRow) => {
    setCommentAlt("");
    setDateAlt("");
    setCreneauAlt("");
    setModal({ kind: "alternative", demand });
  };

  const closeModal = () => setModal(null);

  const submitAccepter = async () => {
    if (!modal || modal.kind !== "accepter") return;
    const { demand } = modal;
    const body: Record<string, unknown> = {};
    if (commentAccepter.trim()) body.commentaireAdmin = commentAccepter.trim();
    if (datePrevueInput.trim()) body.datePrevuePassage = new Date(datePrevueInput).toISOString();

    if (demand.typeDemande === "INTERVENTION_URGENTE") {
      const cid = Number(contratIdInput);
      if (!Number.isInteger(cid) || cid <= 0) {
        toast.warning("Indiquez le numéro de contrat actif du client pour créer le passage.");
        return;
      }
      body.contratId = cid;

      const planNum = urgTarifReferentPlanId.trim() ? Number(urgTarifReferentPlanId) : NaN;
      const amtRaw = urgTarifMontant.trim().replace(",", ".");
      const amtVal = amtRaw === "" ? NaN : Number(amtRaw);
      const hasPlan = Number.isInteger(planNum) && planNum > 0;
      const hasAmt = Number.isFinite(amtVal) && amtVal > 0;
      if (hasPlan && hasAmt) {
        toast.warning("Choisissez soit un plan catalogue soit un montant manuel.");
        return;
      }
      if (hasAmt) {
        const d = urgTarifDevise.trim().toUpperCase().slice(0, 12);
        body.tarifIntervention = {
          montantForfait: amtVal,
          ...(d ? { devise: d } : {}),
        };
      } else if (hasPlan) {
        body.tarifIntervention = { referentPlanId: planNum };
      }
    }

    if (
      demand.typeDemande === "MODIFICATION_PASSAGE" &&
      !demand.nouvelleDateDemandee &&
      !datePrevueInput.trim() &&
      !demand.creneauDemande?.trim()
    ) {
      toast.warning(
        "Indiquez une date de passage ou assurez-vous que la demande comporte un créneau à enregistrer."
      );
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/demandes-client/${demand.id}/accepter`, body);
      toast.success("Demande acceptée — passage mis à jour ou créé.");
      closeModal();
      void load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      toast.error(err.response?.data?.message ?? err.response?.data?.error ?? "Action impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitRefuser = async () => {
    if (!modal || modal.kind !== "refuser") return;
    if (!commentRefuser.trim()) {
      toast.warning("Le commentaire admin est obligatoire pour un refus.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/demandes-client/${modal.demand.id}/refuser`, {
        commentaireAdmin: commentRefuser.trim(),
      });
      toast.success("Demande refusée.");
      closeModal();
      void load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? "Action impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAlternative = async () => {
    if (!modal || modal.kind !== "alternative") return;
    if (!commentAlt.trim()) {
      toast.warning("Le commentaire admin est obligatoire pour une proposition alternative.");
      return;
    }
    if (!dateAlt.trim()) {
      toast.warning("La date alternative proposée est obligatoire.");
      return;
    }
    if (!creneauAlt.trim()) {
      toast.warning("Le créneau alternatif est obligatoire.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/demandes-client/${modal.demand.id}/proposer-alternative`, {
        commentaireAdmin: commentAlt.trim(),
        dateAlternativeProposee: new Date(dateAlt).toISOString(),
        creneauAlternatif: creneauAlt.trim(),
      });
      toast.success("Proposition alternative enregistrée.");
      closeModal();
      void load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? "Action impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  const pending = summary?.enAttente ?? 0;

  const passageEffectBadge = (d: DemandeClientRow): string => {
    if (d.typeDemande === "INTERVENTION_URGENTE") {
      return "Acceptera → création d’un passage exceptionnel (contrat requis)";
    }
    return "Acceptera → mise à jour du passage lié";
  };

  return (
    <section
      className="rounded-2xl border border-amber-500/35 bg-gradient-to-br from-amber-950/40 via-slate-900/90 to-slate-900 p-4 shadow-lg md:p-5"
      aria-labelledby="client-demandes-heading"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-300">
            <ClipboardList className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 id="client-demandes-heading" className="text-sm font-semibold text-amber-100/95">
              Demandes client à traiter
            </h2>
            <p className="mt-1 max-w-2xl text-xs text-slate-400">
              Validation interne → passage créé ou mis à jour uniquement (aucune intervention créée ici).{" "}
              <span className="text-slate-500">
                Client demande → Admin valide → Passage → Terrain → Superviseur → Facturation.
              </span>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pending > 0 ? (
            <span className="inline-flex items-center rounded-full bg-amber-500/25 px-3 py-1 text-xs font-semibold text-amber-200 ring-1 ring-amber-400/40">
              {pending} en attente
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-700/80 px-3 py-1 text-xs text-slate-400">
              0 en attente
            </span>
          )}
          <Link
            to={crossLinkTo}
            className="text-xs font-medium text-emerald-400/95 underline-offset-2 hover:underline"
          >
            {crossLinkLabel}
          </Link>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg border border-slate-600 bg-slate-800/80 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800"
          >
            Actualiser
          </button>
        </div>
      </div>

      {!canTreat && (
        <p className="mt-3 text-xs text-slate-500">
          Actions réservées aux rôles admin, planificateur et superviseur.
        </p>
      )}

      {loadErr && (
        <p className="mt-3 flex items-center gap-2 text-xs text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Impossible de charger les demandes (droits ou serveur).
        </p>
      )}

      {!loadErr && summary && (
        <div className="mt-4 flex flex-wrap gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-700/80 bg-slate-900/50 px-3 py-2 text-xs text-slate-300">
            <span className="text-slate-500">Modifications passage</span>
            <span className="font-mono font-semibold text-white">
              {summary.parType.MODIFICATION_PASSAGE}
            </span>
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-orange-700/40 bg-slate-900/50 px-3 py-2 text-xs text-slate-300">
            <Flame className="h-3.5 w-3.5 text-orange-400" aria-hidden />
            <span className="text-slate-500">Urgences client</span>
            <span className="font-mono font-semibold text-orange-200/95">
              {summary.parType.INTERVENTION_URGENTE}
            </span>
          </div>
        </div>
      )}

      {!loadErr && summary && pending === 0 && (
        <p className="mt-4 rounded-xl border border-dashed border-slate-600/80 bg-slate-900/30 px-4 py-3 text-sm text-slate-500">
          Aucune demande client en attente. Le portail client alimentera ce flux (demandes soumises par les
          clients, jamais de modification directe du planning).
        </p>
      )}

      {!loadErr && preview.length > 0 && (
        <ul className="mt-4 space-y-3 border-t border-slate-700/60 pt-4">
          {preview.map((d) => (
            <li
              key={d.id}
              className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-3 text-xs text-slate-400"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <span className="font-semibold text-slate-200">
                    #{d.id}{" "}
                    <span className="font-normal text-slate-500">
                      {d.typeDemande === "INTERVENTION_URGENTE"
                        ? "Hors abonnement"
                        : "Modification"}
                      {d.niveauUrgence !== "NORMAL" && d.typeDemande === "INTERVENTION_URGENTE"
                        ? ` · ${d.niveauUrgence}`
                        : ""}
                    </span>
                  </span>
                  <p className="mt-1 text-slate-300">{d.motif}</p>
                  <dl className="mt-2 grid gap-1 text-[11px] text-slate-500 sm:grid-cols-2">
                    <div>
                      <dt className="inline text-slate-600">Client : </dt>
                      <dd className="inline text-slate-400">
                        {d.client?.nomEntreprise || d.client?.nom || `#${d.client?.id ?? "—"}`}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline text-slate-600">Date demandée : </dt>
                      <dd className="inline text-slate-400">{fmtDate(d.nouvelleDateDemandee)}</dd>
                    </div>
                    {d.ancienneDate && (
                      <div>
                        <dt className="inline text-slate-600">Ancienne date : </dt>
                        <dd className="inline text-slate-400">{fmtDate(d.ancienneDate)}</dd>
                      </div>
                    )}
                    {d.creneauDemande && (
                      <div>
                        <dt className="inline text-slate-600">Créneau : </dt>
                        <dd className="inline text-slate-400">{d.creneauDemande}</dd>
                      </div>
                    )}
                    {d.adresseDemande && d.typeDemande === "INTERVENTION_URGENTE" && (
                      <div className="sm:col-span-2">
                        <dt className="inline text-slate-600">Adresse : </dt>
                        <dd className="inline text-slate-400">{d.adresseDemande}</dd>
                      </div>
                    )}
                  </dl>
                  <p className="mt-2 rounded-lg bg-emerald-950/40 px-2 py-1 text-[11px] text-emerald-200/90 ring-1 ring-emerald-800/50">
                    {passageEffectBadge(d)}
                  </p>
                </div>
                {canTreat && (
                  <div className="flex shrink-0 flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setDrawerDemand(d);
                        setModal(null);
                      }}
                      className="rounded-lg border border-slate-600 bg-slate-800/60 px-2.5 py-1.5 text-[11px] font-medium text-slate-200 hover:bg-slate-800"
                    >
                      Détail
                    </button>
                    <button
                      type="button"
                      onClick={() => openAccepter(d)}
                      className="rounded-lg bg-emerald-700/90 px-2.5 py-1.5 text-[11px] font-medium text-white hover:bg-emerald-600"
                    >
                      Accepter
                    </button>
                    <button
                      type="button"
                      onClick={() => openAlternative(d)}
                      className="rounded-lg bg-amber-700/80 px-2.5 py-1.5 text-[11px] font-medium text-white hover:bg-amber-600"
                    >
                      Proposer alternative
                    </button>
                    <button
                      type="button"
                      onClick={() => openRefuser(d)}
                      className="rounded-lg bg-slate-700 px-2.5 py-1.5 text-[11px] font-medium text-slate-200 hover:bg-slate-600"
                    >
                      Refuser
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demande-modal-title"
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-600 bg-slate-900 p-5 text-slate-200 shadow-2xl">
            <div className="flex items-start justify-between gap-2">
              <h3 id="demande-modal-title" className="text-sm font-semibold text-white">
                {modal.kind === "accepter" && "Accepter la demande"}
                {modal.kind === "refuser" && "Refuser la demande"}
                {modal.kind === "alternative" && "Proposer une alternative"}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-500">#{modal.demand.id} · {modal.demand.motif}</p>

            {modal.kind === "accepter" && (
              <div className="mt-4 space-y-3 text-xs">
                {modal.demand.typeDemande === "INTERVENTION_URGENTE" && (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-slate-300">Intervention hors abonnement</span>
                      <Link
                        to="/admin/categories-tarifs"
                        title="Plans et tarifs catalogue — associer nom + prix (forfait hors abonnement)"
                        className="shrink-0 rounded-lg border border-amber-500/40 bg-amber-950/50 px-2 py-1 text-[10px] font-semibold text-amber-100 hover:border-amber-400/70 hover:bg-amber-900/50"
                      >
                        Tarif intervention urgente
                      </Link>
                    </div>
                    <label className="block">
                      <span className="text-slate-400">Contrat ID (actif du client) *</span>
                      <input
                        type="number"
                        min={1}
                        value={contratIdInput}
                        onChange={(e) => setContratIdInput(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                      />
                    </label>
                    <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 px-2 py-2">
                      <p className="text-[10px] font-semibold text-amber-100">Tarif / forfait (recommandé)</p>
                      <label className="mt-2 block">
                        <span className="text-slate-400">Plan catalogue</span>
                        <select
                          value={urgTarifReferentPlanId}
                          onChange={(e) => setUrgTarifReferentPlanId(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                        >
                          <option value="">— Aucun —</option>
                          {urgPlansCatalog.map((p) => (
                            <option key={p.id} value={String(p.id)}>
                              {p.nom} — {Number(p.montant).toLocaleString("fr-FR")} {p.devise}
                            </option>
                          ))}
                        </select>
                      </label>
                      {urgPlansLoading ? (
                        <span className="mt-1 block text-[10px] text-slate-500">Chargement catalogue…</span>
                      ) : null}
                      <label className="mt-2 block">
                        <span className="text-slate-400">Ou montant forfait</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={urgTarifMontant}
                          onChange={(e) => setUrgTarifMontant(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                          placeholder="Ex. 85"
                        />
                      </label>
                      <label className="mt-2 block">
                        <span className="text-slate-400">Devise (montant manuel)</span>
                        <input
                          type="text"
                          value={urgTarifDevise}
                          onChange={(e) => setUrgTarifDevise(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                          placeholder="USD ou CDF"
                        />
                      </label>
                    </div>
                  </div>
                )}
                <label className="block">
                  <span className="text-slate-400">
                    Date du passage (optionnel — sinon date demandée sur la fiche)
                  </span>
                  <input
                    type="datetime-local"
                    value={datePrevueInput}
                    onChange={(e) => setDatePrevueInput(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                  />
                </label>
                <label className="block">
                  <span className="text-slate-400">Commentaire interne (optionnel)</span>
                  <textarea
                    value={commentAccepter}
                    onChange={(e) => setCommentAccepter(e.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                  />
                </label>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void submitAccepter()}
                  className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {submitting ? "…" : "Confirmer l’acceptation"}
                </button>
              </div>
            )}

            {modal.kind === "refuser" && (
              <div className="mt-4 space-y-3 text-xs">
                <label className="block">
                  <span className="text-slate-400">Motif du refus (obligatoire — aussi transmis au client)</span>
                  <textarea
                    value={commentRefuser}
                    onChange={(e) => setCommentRefuser(e.target.value)}
                    rows={4}
                    required
                    className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                  />
                </label>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void submitRefuser()}
                  className="w-full rounded-xl bg-red-900/80 py-2.5 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50"
                >
                  {submitting ? "…" : "Confirmer le refus"}
                </button>
              </div>
            )}

            {modal.kind === "alternative" && (
              <div className="mt-4 space-y-3 text-xs">
                <label className="block">
                  <span className="text-slate-400">Commentaire admin *</span>
                  <textarea
                    value={commentAlt}
                    onChange={(e) => setCommentAlt(e.target.value)}
                    rows={4}
                    className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                  />
                </label>
                <label className="block">
                  <span className="text-slate-400">Date alternative proposée *</span>
                  <input
                    type="datetime-local"
                    value={dateAlt}
                    onChange={(e) => setDateAlt(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                  />
                </label>
                <label className="block">
                  <span className="text-slate-400">Créneau alternatif *</span>
                  <input
                    type="text"
                    value={creneauAlt}
                    onChange={(e) => setCreneauAlt(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-slate-100"
                  />
                </label>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void submitAlternative()}
                  className="w-full rounded-xl bg-amber-600 py-2.5 text-sm font-semibold text-white hover:bg-amber-500 disabled:opacity-50"
                >
                  {submitting ? "…" : "Enregistrer la proposition"}
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={closeModal}
              className="mt-3 w-full rounded-lg border border-slate-600 py-2 text-xs text-slate-400 hover:bg-slate-800"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {drawerDemand && (
        <div className="fixed inset-0 z-40 flex justify-end" role="presentation">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Fermer le panneau"
            onClick={() => setDrawerDemand(null)}
          />
          <aside
            className="relative z-10 flex h-full w-full max-w-md flex-col border-l border-slate-700 bg-slate-950 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demande-drawer-title"
          >
            <div className="flex items-start justify-between gap-2 border-b border-slate-800 p-4">
              <div>
                <h3 id="demande-drawer-title" className="text-sm font-semibold text-white">
                  Demande #{drawerDemand.id}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {drawerDemand.typeDemande === "INTERVENTION_URGENTE"
                    ? "Intervention hors abonnement"
                    : "Modification de passage"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerDemand(null)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 text-xs text-slate-300">
              <p className="whitespace-pre-wrap text-slate-200">{drawerDemand.motif}</p>
              <dl className="mt-4 grid gap-2 border-t border-slate-800 pt-4 text-[11px]">
                <div>
                  <dt className="text-slate-500">Client</dt>
                  <dd className="text-slate-300">
                    {drawerDemand.client?.nomEntreprise ||
                      drawerDemand.client?.nom ||
                      `#${drawerDemand.client?.id ?? "—"}`}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Créée le</dt>
                  <dd>{fmtDate(drawerDemand.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Date demandée</dt>
                  <dd>{fmtDate(drawerDemand.nouvelleDateDemandee)}</dd>
                </div>
                {drawerDemand.creneauDemande && (
                  <div>
                    <dt className="text-slate-500">Créneau demandé</dt>
                    <dd>{drawerDemand.creneauDemande}</dd>
                  </div>
                )}
                {drawerDemand.adresseDemande && drawerDemand.typeDemande === "INTERVENTION_URGENTE" && (
                  <div className="sm:col-span-2">
                    <dt className="text-slate-500">Adresse</dt>
                    <dd>{drawerDemand.adresseDemande}</dd>
                  </div>
                )}
                {drawerDemand.passage && (
                  <div>
                    <dt className="text-slate-500">Passage lié</dt>
                    <dd>
                      #{drawerDemand.passage.id} — {fmtDate(drawerDemand.passage.datePrevue)} ({drawerDemand.passage.statut})
                    </dd>
                  </div>
                )}
              </dl>
              <p className="mt-4 rounded-lg bg-emerald-950/40 px-2 py-2 text-[11px] text-emerald-200/90 ring-1 ring-emerald-800/50">
                {passageEffectBadge(drawerDemand)}
              </p>
            </div>
            {canTreat && (
              <div className="flex flex-wrap gap-2 border-t border-slate-800 p-4">
                <button
                  type="button"
                  onClick={() => {
                    openAccepter(drawerDemand);
                    setDrawerDemand(null);
                  }}
                  className="rounded-lg bg-emerald-700/90 px-3 py-2 text-[11px] font-medium text-white hover:bg-emerald-600"
                >
                  Accepter
                </button>
                <button
                  type="button"
                  onClick={() => {
                    openAlternative(drawerDemand);
                    setDrawerDemand(null);
                  }}
                  className="rounded-lg bg-amber-700/80 px-3 py-2 text-[11px] font-medium text-white hover:bg-amber-600"
                >
                  Proposer alternative
                </button>
                <button
                  type="button"
                  onClick={() => {
                    openRefuser(drawerDemand);
                    setDrawerDemand(null);
                  }}
                  className="rounded-lg bg-slate-700 px-3 py-2 text-[11px] font-medium text-slate-200 hover:bg-slate-600"
                >
                  Refuser
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}
